import { useState, useRef, FormEvent } from 'react';
import { UserContext } from '@kinapp/shared';

interface CircleCardProps {
  context: UserContext;
  urgentCount: number;
  onClick: () => void;
  onEditLabel?: (label: string | null) => Promise<void>;
}

export function CircleCard({ context, urgentCount, onClick, onEditLabel }: CircleCardProps) {
  const displayName = context.label ?? context.name;
  const [editing, setEditing] = useState(false);
  const [labelValue, setLabelValue] = useState(context.label ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLabelValue(context.label ?? '');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(false);
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onEditLabel) return;
    setSaving(true);
    try {
      const trimmed = labelValue.trim();
      await onEditLabel(trimmed || null);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={editing ? undefined : onClick}
        className={`w-full text-left p-4 rounded-xl border bg-white transition-all ${
          editing
            ? 'border-primary-400 shadow-sm'
            : 'border-gray-200 hover:border-primary-300 hover:shadow-sm'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {editing ? (
              <form onSubmit={saveEdit} onClick={(e) => e.stopPropagation()}>
                <input
                  ref={inputRef}
                  type="text"
                  value={labelValue}
                  onChange={(e) => setLabelValue(e.target.value)}
                  placeholder={context.name}
                  maxLength={50}
                  className="w-full text-sm font-semibold text-gray-900 border-0 border-b border-primary-400 bg-transparent focus:outline-none focus:border-primary-600 pb-0.5"
                />
                <p className="text-xs text-gray-400 mt-1">{context.name}</p>
                <div className="flex gap-2 mt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="text-xs text-primary-600 font-medium hover:text-primary-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Cancel
                  </button>
                  {context.label && (
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!onEditLabel) return;
                        setSaving(true);
                        try { await onEditLabel(null); } finally { setSaving(false); setEditing(false); }
                      }}
                      className="text-xs text-gray-400 hover:text-red-500 ml-auto"
                    >
                      Remove label
                    </button>
                  )}
                </div>
              </form>
            ) : (
              <>
                <h3 className="font-semibold text-gray-900 truncate">{displayName}</h3>
                {context.label && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">{context.name}</p>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {urgentCount > 0 && !editing && (
              <span className="flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-accent-500 text-[10px] font-bold text-white">
                {urgentCount}
              </span>
            )}
            {onEditLabel && !editing && (
              <button
                onClick={startEdit}
                className="p-1 rounded text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                title="Rename this circle"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </div>
        </div>
        {!editing && (
          <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
            <span>
              {context.dependentCount} {context.dependentCount === 1 ? 'person' : 'people'}
            </span>
            <span className="text-gray-300">·</span>
            <span>
              {context.memberCount} {context.memberCount === 1 ? 'member' : 'members'}
            </span>
            <span className="text-gray-300">·</span>
            <span className="capitalize">{context.role.toLowerCase()}</span>
          </div>
        )}
      </button>
    </div>
  );
}
